/**
 * Ktor-backed repository for all architecture API calls.
 *
 * Each function returns a [Flow] that emits [APIState.Loading] first, then the
 * result. The flow completes after one emission pair — the ViewModel's
 * viewModelScope handles retries/refresh by calling the function again.
 *
 * [httpClient] is injected by Koin as a shared singleton.
 */
package org.moxultimate.topwithmox.architecture.api

import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.architecture.data.ArchitectureRequest
import org.moxultimate.topwithmox.architecture.data.ArchitectureResponse
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState

class ArchitectureRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/architecture"

    /** GET /architectures — emits [APIState.Loading] then the result. */
    fun getAllArchitectures(): Flow<APIState<List<ArchitectureResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl)
        })
    }

    /** GET /architectures/{architectureId}/architecture */
    fun getSpecificArchitecture(architectureId: Int): Flow<APIState<ArchitectureResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get("$baseUrl/$architectureId")
        })
    }

    /** POST /architectures — body serialized via ContentNegotiation. */
    fun createArchitecture(request: ArchitectureRequest): Flow<APIState<ArchitectureResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(baseUrl) {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        })
    }

    /** DELETE /architectures/{architectureId} */
    fun deleteArchitecture(architectureId: Int): Flow<APIState<ArchitectureResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.delete("$baseUrl/$architectureId")
        })
    }
}
